import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { httpGet, httpPost, httpPut } from "../../services/httpService";
import {
  getPharmacyMetricThresholdByCompanyIdUrl,
  updatePharmacyMetricThresholdUrl,
  getCompanyMetricVisibilityUrl,
  upsertCompanyMetricVisibilityUrl,
  refreshPharmacyMetricThresholdUrl,
  checkRefreshStatusUrl,
} from "../../apiUrls/apiUrls";

const initialState = {
  isDashboardKPIsLoading: false,
  pharmacyMetricThresholdData: null,
  kpiServiceCount: 0,
  kpiServiceColor: "",
  updateKPILoader: false,
  companyMetricVisibilityData: null,
  isCompanyMetricVisibilityLoading: false,
  isRefreshMetricLoading: false,
  refreshResponse: null,
  isRefreshStatusLoading: false,
  refreshStatusResponse: null,
  isRefreshAvailable: true,
  kpiDataFetchedAt: null,
};

export const getPharmacyMetricThresholdByCompanyIdData = createAsyncThunk(
  "pharmacyMetricThreshold/getPharmacyMetricThresholdByCompanyIdData",
  async (companyId, thunkAPI) => {
    const urlToGet = getPharmacyMetricThresholdByCompanyIdUrl(companyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const updatePharmacyMetricThresholdData = createAsyncThunk(
  "pharmacyMetricThreshold/updatePharmacyMetricThresholdData",
  async (data, thunkApi) => {
    const url = updatePharmacyMetricThresholdUrl();
    const respData = await httpPut(url, data);
    return respData;
  },
);

export const getCompanyMetricVisibilityData = createAsyncThunk(
  "pharmacyMetricThreshold/getCompanyMetricVisibilityData",
  async (companyId, thunkAPI) => {
    const urlToGet = getCompanyMetricVisibilityUrl(companyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const upsertCompanyMetricVisibilityData = createAsyncThunk(
  "pharmacyMetricThreshold/upsertCompanyMetricVisibilityData",
  async (data, thunkAPI) => {
    const url = upsertCompanyMetricVisibilityUrl();
    const respData = await httpPost(url, data);
    return respData;
  },
);

export const refreshPharmacyMetricThresholdData = createAsyncThunk(
  "pharmacyMetricThreshold/refreshPharmacyMetricThresholdData",
  async (data, thunkAPI) => {
    const url = refreshPharmacyMetricThresholdUrl(data);
    const respData = await httpPost(url, data);
    return respData;
  },
);

export const checkRefreshStatusData = createAsyncThunk(
  "pharmacyMetricThreshold/checkRefreshStatusData",
  async (companyId, thunkAPI) => {
    const urlToGet = checkRefreshStatusUrl(companyId);
    const respData = await httpGet(urlToGet);

    return respData;
  },
);

export const dashboardKPISlice = createSlice({
  name: "dashboardKPI",
  initialState,
  reducers: {
    setKpiServiceCountData: (state, action) => {
      state.kpiServiceCount = action?.payload || 0;
    },
    setKpiServiceColorData: (state, action) => {
      state.kpiServiceColor = action?.payload || "";
    },
    setIsRefreshAvailableData: (state, action) => {
      state.isRefreshAvailable = action?.payload ?? true;
    },

    resetDashboardKPIState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPharmacyMetricThresholdByCompanyIdData.pending, (state) => {
        state.isDashboardKPIsLoading = true;
      })
      .addCase(
        getPharmacyMetricThresholdByCompanyIdData.fulfilled,
        (state, action) => {
          state.isDashboardKPIsLoading = false;
          state.pharmacyMetricThresholdData = action?.payload;
          state.kpiDataFetchedAt = new Date().toISOString();
        },
      )
      .addCase(
        getPharmacyMetricThresholdByCompanyIdData.rejected,
        (state, action) => {
          state.isDashboardKPIsLoading = false;
          state.pharmacyMetricThresholdData = [];
        },
      )
      .addCase(updatePharmacyMetricThresholdData.pending, (state) => {
        state.updateKPILoader = true;
      })
      .addCase(updatePharmacyMetricThresholdData.fulfilled, (state, action) => {
        state.updateKPILoader = false;
      })
      .addCase(updatePharmacyMetricThresholdData.rejected, (state, action) => {
        state.updateKPILoader = false;
      })
      .addCase(getCompanyMetricVisibilityData.pending, (state) => {
        state.isCompanyMetricVisibilityLoading = true;
      })
      .addCase(getCompanyMetricVisibilityData.fulfilled, (state, action) => {
        state.isCompanyMetricVisibilityLoading = false;
        state.companyMetricVisibilityData = action?.payload;
      })
      .addCase(getCompanyMetricVisibilityData.rejected, (state, action) => {
        state.isCompanyMetricVisibilityLoading = false;
        state.companyMetricVisibilityData = [];
      })
      .addCase(upsertCompanyMetricVisibilityData.pending, (state) => {
        state.isCompanyMetricVisibilityLoading = true;
      })
      .addCase(upsertCompanyMetricVisibilityData.fulfilled, (state, action) => {
        state.isCompanyMetricVisibilityLoading = false;
      })
      .addCase(upsertCompanyMetricVisibilityData.rejected, (state, action) => {
        state.isCompanyMetricVisibilityLoading = false;
      })
      .addCase(refreshPharmacyMetricThresholdData.pending, (state) => {
        state.isRefreshMetricLoading = true;
      })
      .addCase(
        refreshPharmacyMetricThresholdData.fulfilled,
        (state, action) => {
          state.isRefreshMetricLoading = false;
          state.refreshResponse = action?.payload;
        },
      )
      .addCase(refreshPharmacyMetricThresholdData.rejected, (state, action) => {
        state.isRefreshMetricLoading = false;
      })
      .addCase(checkRefreshStatusData.pending, (state) => {
        state.isRefreshStatusLoading = true;
      })
      .addCase(checkRefreshStatusData.fulfilled, (state, action) => {
        state.isRefreshStatusLoading = false;

        state.refreshStatusResponse = action?.payload;
      })
      .addCase(checkRefreshStatusData.rejected, (state, action) => {
        state.isRefreshStatusLoading = false;
        state.refreshStatusResponse = null;
      });
  },
});

// Action creators are generated for each case reducer function
export const {
  setKpiServiceCountData,
  setKpiServiceColorData,
  setIsRefreshAvailableData,
} = dashboardKPISlice.actions;

export default dashboardKPISlice.reducer;
